import { StatusTone } from 'src/shared/status-badge/status-badge.component';
import {
  DISBURSEMENT_ROUND_STATUS_LABELS,
  DisbursementRoundStatusCode,
} from './disbursement-round.model';
import {
  DISBURSEMENT_STATUS_LABELS,
  DisbursementRosterRow,
  DisbursementStatusCode,
} from './disbursement.model';
import { DISBURSEMENT_DETAILED_STATUS_ROLE_IDS, RoleID } from 'src/utils/enum/role.enum';

// Labels stay in the model files (they mirror the backend's STATUS_LABELS);
// this module only decides how each one is coloured.
export interface DisbursementStatusDisplay {
  label: string;
  tone: StatusTone;
}

// Exhaustive by design: the registry pages used to carry a Partial map covering
// only 2 of the codes, so a sent-back cert rendered as an ordinary "waiting"
// chip. `pending` = the actor themselves must act; `waiting_review` = it is
// sitting with an approver. That distinction belongs to the DETAILED audience
// alone — the summary maps below flatten every in-flight state to one `pending`
// on purpose, so colour cannot disclose the station the label just hid.
const TONE_BY_STATUS: Record<DisbursementStatusCode, StatusTone> = {
  pending_teacher: 'pending',
  pending_finance: 'waiting_review',
  // v2.0: phase 1 ends in the pool — done with the chain, waiting for สารบรรณ to
  // cut a รอบส่งเบิก, so it is a waiting state and not a completed one.
  pending_round: 'waiting_review',
  completed: 'completed',
  revision_schedule_requested: 'revision',
  // Terminal failure, not a completion — mirrors the roster's "expired" treatment.
  conditions_not_met: 'special_revision',
};

// v2.0 phase 2 — the รอบส่งเบิก's own state machine.
const TONE_BY_ROUND_STATUS: Record<DisbursementRoundStatusCode, StatusTone> = {
  draft: 'draft',
  pending_finance_sign: 'waiting_review',
  pending_head: 'waiting_review',
  pending_vice_dean_admin: 'waiting_review',
  completed: 'completed',
  returned_to_officer: 'revision',
  cancelled: 'special_revision',
};

// DISBURSEMENT_DETAILED_STATUS_ROLE_IDS is intentionally empty — no role resolves to 'detailed' — context/decisions/DECISION_RolesPermissions.md
export type DisbursementStatusAudience = 'detailed' | 'summary';

// Exhaustive like the tone maps above, and for the same reason: a new status code
// must break the build here instead of leaking its station name to a summary
// reader. `null` = no override, fall through to the detailed display.
const SUMMARY_OVERRIDE_BY_STATUS: Record<DisbursementStatusCode, DisbursementStatusDisplay | null> = {
  pending_teacher: { label: 'รอดำเนินการ', tone: 'pending' },
  pending_finance: { label: 'รอดำเนินการ', tone: 'pending' },
  pending_round: { label: 'รอดำเนินการ', tone: 'pending' },
  completed: null,
  revision_schedule_requested: { label: 'ส่งกลับแก้ไข', tone: 'revision' },
  // Terminal, not a hidden station — the teacher already got the "ไม่ผ่านเงื่อนไข" email,
  // so summary shows the same real label as detailed (like completed: null).
  conditions_not_met: null,
};

const SUMMARY_OVERRIDE_BY_ROUND_STATUS: Record<
  DisbursementRoundStatusCode,
  DisbursementStatusDisplay | null
> = {
  draft: null,
  pending_finance_sign: { label: 'รอดำเนินการ', tone: 'pending' },
  pending_head: { label: 'รอดำเนินการ', tone: 'pending' },
  pending_vice_dean_admin: { label: 'รอดำเนินการ', tone: 'pending' },
  completed: null,
  returned_to_officer: { label: 'ส่งกลับแก้ไข', tone: 'revision' },
  cancelled: null,
};

// `audience` defaults to 'detailed' deliberately: a call site nobody updated must
// degrade to today's wording, never silently collapse the officer's roster.
export function getDisbursementStatusDisplay(
  status: DisbursementStatusCode | null | undefined,
  audience: DisbursementStatusAudience = 'detailed',
): DisbursementStatusDisplay {
  if (status == null) {
    return { label: '-', tone: 'draft' };
  }

  if (audience === 'summary') {
    const override = SUMMARY_OVERRIDE_BY_STATUS[status];
    if (override != null) {
      // Copied, not handed out: every other branch returns a fresh literal, so a
      // caller must never be able to mutate the map itself through the result.
      return { ...override };
    }
  }

  return { label: DISBURSEMENT_STATUS_LABELS[status], tone: TONE_BY_STATUS[status] };
}

export function getDisbursementRoundStatusDisplay(
  status: DisbursementRoundStatusCode | null | undefined,
  audience: DisbursementStatusAudience = 'detailed',
): DisbursementStatusDisplay {
  if (status == null) {
    return { label: '-', tone: 'draft' };
  }

  if (audience === 'summary') {
    const override = SUMMARY_OVERRIDE_BY_ROUND_STATUS[status];
    if (override != null) {
      return { ...override };
    }
  }

  return { label: DISBURSEMENT_ROUND_STATUS_LABELS[status], tone: TONE_BY_ROUND_STATUS[status] };
}

// Resolved from the ACTIVE role, never the user's full role set — a สารบรรณ who
// switched to another hat reads that hat's vocabulary.
export function disbursementStatusAudienceFor(roleId: RoleID | null): DisbursementStatusAudience {
  return roleId != null && DISBURSEMENT_DETAILED_STATUS_ROLE_IDS.includes(roleId)
    ? 'detailed'
    : 'summary';
}

// v2.0 hub roster phase (Disbursement::RegistryRoster#phase_for) — one badge per phase a
// roster row can be in, shared by the registry page's own roster view and the
// cover-memos hub's roster table so the two screens can never disagree on wording.
// Document-officer-only surface, so it is always the detailed audience — the two
// display calls inside deliberately ride the 'detailed' default and take no
// audience param.
export function getDisbursementRosterPhaseDisplay(
  row: Pick<DisbursementRosterRow, 'phase' | 'readiness_missing' | 'status_code' | 'round_status_code'>,
): DisbursementStatusDisplay {
  switch (row.phase) {
    case 'readiness':
      return row.readiness_missing.length > 0
        ? { tone: 'draft', label: row.readiness_missing.join(', ') }
        : { tone: 'draft', label: 'พร้อม รอถึงวันส่งเบิก' };
    case 'waiting_teacher':
    case 'waiting_finance':
    case 'in_revision':
    case 'disqualified':
      return getDisbursementStatusDisplay(row.status_code);
    case 'ready_for_round':
      return { tone: 'pending', label: 'เสร็จสิ้น รอเข้ารอบ' };
    case 'in_round':
      return getDisbursementRoundStatusDisplay(row.round_status_code);
    case 'completed':
      return { tone: 'completed', label: 'เสร็จสิ้น' };
    case 'missed':
      return { tone: 'inert', label: 'ไม่ผ่านเกณฑ์' };
    case 'expired':
      return { tone: 'special_revision', label: 'ไม่สำเร็จ (หมดเขต)' };
  }
}
